import { useAddressStore } from '@/stores/address-store';
import { useCheckoutStore, type DeliveryDetails } from '@/stores/checkout-store';
import type { SavedAddress, SavedAddressInput } from '@/lib/api/addresses';

/**
 * Подстановка сохранённого адреса в оформление заказа.
 *
 * Смысл всей затеи: человек заполняет подъезд, этаж и домофон ОДИН раз.
 * Дальше он выбирает адрес из книги, и все четыре поля подставляются вместе
 * с ним — а не спрашиваются заново в корзине.
 *
 * Зона доставки при этом перепроверяется на сервере: стоимость доставки и
 * минимальная сумма зависят от неё, и подставлять их из сохранённой записи
 * нельзя — зоны меняются в админке. Клиент только ОТОБРАЖАЕТ то, что ответил
 * сервер.
 */
export async function applySavedAddress(saved: SavedAddress): Promise<boolean> {
  const details: Partial<DeliveryDetails> = {
    address: saved.address,
    apartment: saved.apartment,
    entrance: saved.entrance,
    floor: saved.floor,
    intercom: saved.intercom,
    isPrivateHouse: saved.is_private_house === 1,
    hasIntercom: saved.has_intercom !== 0,
    savedAddressId: saved.id,
  };
  useCheckoutStore.getState().setDeliveryDetails(details);

  const coordinates =
    saved.lat != null && saved.lon != null ? { lat: saved.lat, lon: saved.lon } : undefined;

  const cityId = useAddressStore.getState().selectedCity?.id;
  const result = await useAddressStore.getState().checkAddress(saved.address, coordinates);
  const state = useAddressStore.getState();
  if (result.error || state.selectedCity?.id !== cityId || state.selectedAddress?.address !== saved.address) {
    return false;
  }

  // Координаты возвращаем в стор руками: checkAddress их туда НЕ пишет (это
  // делает только выбор подсказки на карте). Без этого адрес, подставленный
  // из книги, оказывался в сторе без координат — и при следующем сохранении
  // (человек поправил этаж) уехал бы в базу с пустыми lat/lon, после чего
  // подставляться перестал бы совсем.
  if (coordinates) {
    const current = useAddressStore.getState().selectedAddress;
    if (current && current.address === saved.address && !current.coordinates) {
      useAddressStore.getState().setSelectedAddress({ ...current, coordinates });
    }
  }

  return result.isInZone === true;
}

/**
 * Тот же дом в книге, что человек только что выбрал на карте.
 *
 * Сравнение НАМЕРЕННО грубее, чем isSameAddress: квартира отбрасывается.
 * Подсказка карты приходит то с «, кв 314» на конце, то без него, а нам важно
 * узнать дом — уточнения (квартира, подъезд, этаж, домофон) мы как раз и хотим
 * достать из книги.
 */
export function findSavedByPlace(
  addresses: SavedAddress[],
  address: string | undefined
): SavedAddress | undefined {
  const norm = (value: string | undefined) =>
    (value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      // «, кв 314», «, кв. 314», «, квартира 314» — всё это уточнение, не дом.
      .replace(/,\s*кв(артира)?\.?\s*[^,]*$/u, '')
      .trim();

  const target = norm(address);
  if (!target) return undefined;
  return addresses.find((saved) => norm(saved.address) === target);
}

/**
 * Дозаполняет квартиру, подъезд, этаж и домофон из книги, НЕ трогая адрес.
 *
 * Зачем отдельно от applySavedAddress: тот подставляет адрес целиком и заново
 * считает зону. Здесь адрес человек уже выбрал сам — менять его нельзя, нужны
 * только уточнения. Заполняются ТОЛЬКО пустые поля: если человек что-то ввёл
 * руками, его ввод важнее сохранённого.
 *
 * Возвращает true, если хоть что-то подставилось.
 */
export function fillDetailsFromSaved(saved: SavedAddress): boolean {
  const current = useCheckoutStore.getState().deliveryDetails;
  const patch: Partial<DeliveryDetails> = {};

  if (!current.apartment?.trim() && saved.apartment) patch.apartment = saved.apartment;
  if (!current.entrance?.trim() && saved.entrance) patch.entrance = saved.entrance;
  if (!current.floor?.trim() && saved.floor) patch.floor = saved.floor;
  if (!current.intercom?.trim() && saved.intercom) patch.intercom = saved.intercom;

  if (Object.keys(patch).length === 0) return false;

  patch.savedAddressId = saved.id;
  patch.isPrivateHouse = saved.is_private_house === 1;
  patch.hasIntercom = saved.has_intercom !== 0;

  useCheckoutStore.getState().setDeliveryDetails(patch);
  return true;
}

/** Форма для сохранения текущих данных оформления в адресную книгу. */
export function detailsToAddressInput(
  details: DeliveryDetails,
  options: { label: string; type: SavedAddressInput['type']; isDefault?: boolean }
): SavedAddressInput {
  const coordinates = useAddressStore.getState().selectedAddress?.coordinates;

  return {
    label: options.label,
    address: details.address,
    type: options.type,
    apartment: details.isPrivateHouse ? '' : details.apartment,
    entrance: details.isPrivateHouse ? '' : details.entrance,
    floor: details.isPrivateHouse ? '' : details.floor,
    intercom: details.isPrivateHouse || !details.hasIntercom ? '' : details.intercom,
    comment: '',
    is_private_house: details.isPrivateHouse,
    has_intercom: details.hasIntercom,
    is_default: options.isDefault ?? false,
    lat: coordinates?.lat ?? null,
    lon: coordinates?.lon ?? null,
  };
}

/**
 * Обязательные поля адреса (вопрос 15, ответ «В»).
 *
 * В многоквартирном доме курьеру нужны квартира, подъезд и этаж. Домофон нужен
 * там, где он есть: в доме с открытым подъездом человеку нечего туда написать,
 * поэтому «нет домофона» в одно нажатие снимает требование.
 *
 * @returns список полей с ошибками (пустой — можно идти дальше)
 */
export function validateAddressFields(details: DeliveryDetails): string[] {
  if (details.isPrivateHouse) return [];

  const missing: string[] = [];
  if (!details.apartment.trim()) missing.push('apartment');
  if (!details.entrance.trim()) missing.push('entrance');
  if (!details.floor.trim()) missing.push('floor');
  if (details.hasIntercom && !details.intercom.trim()) missing.push('intercom');
  return missing;
}
